import React, { useEffect, useState } from "react";
import LoadingAnimation from "../../components/LoadingAnimation";
import {  CLIENT_ID,} from "../../config/google";
import axios from "axios";
import Button from "../Button/Button";
import { getAxiosTokenInstance } from "../../utils/axiosInstance";
import toast from "react-hot-toast";

declare global {
  interface Window {
    google?: any;
  }
}

interface GoogleDocsHandlerProps {
  docId: string | null;
  setDocId: (id: string | '') => void;
  title: string | null;
  mode?: string | 'edit' | 'view';
}

const GoogleDocsCreator: React.FC<GoogleDocsHandlerProps> = ({
  docId,
  setDocId,
  title,
  mode = "view",
}) => {
  const [loggedInUserEmail, setLoggedInUserEmail] = useState<string | null>(null);
  const [isSigned, setIsSigned] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const email = localStorage.getItem("email");
    if (email) {
      setLoggedInUserEmail(email);
      setIsSigned(true);
    } else {
      const existingScript = document.getElementById("google-identity-script");
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.id = "google-identity-script";
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        script.onload = () => {
          // After script loads, we can initialize
          initGoogleIdentity();
        };
      } else {
        // Script already present
        initGoogleIdentity();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, []);

  // 2) Initialize Google Identity Services, set callback
  const initGoogleIdentity = () => {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredentialResponse,
      });
      // Render sign-in button if not already signed in
      renderSignInButton();
    }
  };

  // 3) Render sign-in button
  const renderSignInButton = () => {
    const signInDiv = document.getElementById("googleSignInDiv");
    if (signInDiv && window.google && window.google.accounts) {
      window.google.accounts.id.renderButton(signInDiv, {
        theme: "outline",
        size: "large",
      });
    }
  };

  // 4) Decode credential response
  const handleCredentialResponse = (response: any) => {
    const token = response.credential;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = JSON.parse(window.atob(base64));
    const userEmail = decodedPayload.email;

    setLoggedInUserEmail(userEmail);
    localStorage.setItem("email", userEmail);
    setIsSigned(true);
    console.log("User email:", userEmail);
  };



  // Create a new doc via your Fastify backend and share with the logged-in user
  const createGoogleDoc = async () => {
    if (!loggedInUserEmail) {
      return toast.error("No user email found. Please sign in first.");
    }
    setLoading(true);
    const instance = await getAxiosTokenInstance();
    try {
      const { data } = await instance.post("/googleDocs/createDoc", {
        title,
        email: loggedInUserEmail,
      })
      if (data.docId) {
        setDocId(data.docId);
        toast.success(`Document created successfully`);
      } else {
        console.error("Error creating document:", data.error);
        toast.error(data.error || "Error creating document");
      }
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error("Error creating document");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!loggedInUserEmail) {
      return toast.error("No user email found. Please sign in first.");
    }
    setLoading(true);
    const instance = await getAxiosTokenInstance();

    try {
      const { data } = await instance.post("/googleDocs/shareDoc", {
        docId,
        email: loggedInUserEmail,
      })
      if (data.docId) {
        setDocId(data.docId);
        toast.success(`You can now edit`);
      } else {
        console.error("Error creating document:", data.error);
        toast.error(data.error || "Error sharing document");
      }
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error("Error creating document");
    } finally {
      setLoading(false);
    }
  }


  // Open doc in a new tab for editing or viewing
  const openGoogleDoc = () => {
    if (!docId) {
      console.error("No document ID available.");
      return;
    }
    // "edit" or "view"
    const showMode = mode ? "edit" : "view";
    const docUrl = `https://docs.google.com/document/d/${docId}/${showMode}`;
    window.open(docUrl, "_blank");
  };

  return (
    <div className="flex flex-col items-center">
      {loading && (
        <div>
          <LoadingAnimation customHeight={"150px"} customWidth={"150px"} />
        </div>
      )}

      {!loading && (
        <>
          {/* If not signed in, show sign-in button or the rendered Google button */}
          {!isSigned ? (
            <div>
              <div id="googleSignInDiv" style={{ marginBottom: "1rem" }}></div>

            </div>
          ) : docId ? (
            <>
              <Button
                onClick={openGoogleDoc}
                type="save"
              >
                {mode == 'edit' ? "Edit Google Doc" : "View Google Doc"}
              </Button>
          
              <a className="blue" onClick={handleRequestEdit}>Request Edit</a>
            </>
          ) : (
            <>
              <Button
                onClick={createGoogleDoc}
                type="save">
                Create Google Doc
              </Button>

            </>
          )}
        </>
      )}
    </div>
  );
};

export default GoogleDocsCreator;
